'use client';

import CheckIcon from '@mui/icons-material/Check';
import { Box, ListItemButton, Typography } from '@mui/material';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { useHYSACalculatorStore } from '../calculatorStore';
import { formatToDisplayValue } from '../utils';

type TimePeriodDropdownOptions = {
  activeIndex: number;
  options: {
    label: string;
    value: number;
  }[];
};

export function TimePeriodDropdownOptions({ activeIndex, options }: TimePeriodDropdownOptions) {
  const fireHysaCalculatorInteractedPageAction = usePageAction({
    actionName: 'hysa_calculator_interacted',
    actionSection: 'banking',
    actionType: 'select',
  });
  const {
    isOpened,
    timePeriod,
    handleTimePeriodOpen,
    initialDeposit,
    monthlyDeposit,
    annualPercentageYield,
  } = useHYSACalculatorStore(
    useShallow(state => ({
      annualPercentageYield: state.formFields.annualPercentageYield,
      initialDeposit: state.formFields.initialDeposit.includes(',')
        ? state.formFields.initialDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.initialDeposit),
      monthlyDeposit: state.formFields.monthlyDeposit.includes(',')
        ? state.formFields.monthlyDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.monthlyDeposit),
      isOpened: state.isTimePeriodOpen,
      timePeriod: state.formFields.timePeriod,
      handleTimePeriodOpen: state.handleTimePeriodOpen,
    })),
  );

  const listOptionsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const setItemListItemRef =
    (listItemIndex: number): React.RefCallback<HTMLDivElement> =>
    listItemRef => {
      listOptionsRefs.current[listItemIndex] = listItemRef;
    };

  useEffect(() => {
    if (isOpened) {
      listOptionsRefs.current[activeIndex]?.focus({ preventScroll: true });
    }
  }, [isOpened, activeIndex]);

  return (
    <>
      <Box
        sx={{
          bgcolor: 'white',
          paddingX: '1rem',
          paddingY: '.5rem',
          width: '100%',
        }}
      >
        <Typography
          variant="text"
          sx={{
            color: 'text.secondary',
            fontSize: '.875rem',
            fontWeight: 500,
            letterSpacing: '0.07px',
            lineHeight: '1.125rem',
          }}
        >
          Time period
        </Typography>
      </Box>
      {options.map(({ value, label }, idx) => {
        const valueStr = value.toString();
        const selected = timePeriod === valueStr;
        return (
          <ListItemButton
            id={`option-${value}`}
            key={label}
            onClick={() => {
              fireHysaCalculatorInteractedPageAction({
                additional_attributes: {
                  annual_percentage_yield_value_input: annualPercentageYield,
                  initial_deposit_value_input: initialDeposit,
                  monthly_deposit_value_input: monthlyDeposit,
                  time_period_value_input: valueStr,
                },
              });
              handleTimePeriodOpen('timePeriod', valueStr, false);
            }}
            ref={setItemListItemRef(idx)}
            role="option"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              width: '100%',
            }}
            selected={selected}
            tabIndex={idx === activeIndex ? 0 : -1}
          >
            <Typography
              variant="text"
              key={label}
              sx={{
                color: 'text.secondary',
                fontSize: '.875rem',
                fontWeight: 350,
                letterSpacing: '.07px',
                width: '100%',
              }}
            >
              {label}
            </Typography>
            <Box sx={{ width: 24, display: 'flex', justifyContent: 'flex-end' }}>
              {selected && <CheckIcon fontSize="small" sx={{ color: 'greens.main' }} />}
            </Box>
          </ListItemButton>
        );
      })}
    </>
  );
}
